import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Calendar,
  Play,
  Pause,
  Trash2,
  Edit,
  Plus,
  AlertCircle,
  CheckCircle,
  FileText,
  Users
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format } from 'date-fns';

interface ScheduledReport {
  id: string;
  name: string;
  description: string;
  reportType: 'player' | 'team' | 'academy' | 'session';
  frequency: 'daily' | 'weekly' | 'monthly' | 'custom';
  customSchedule?: string; // Cron expression for custom schedules
  nextRun: string;
  lastRun?: string;
  recipients: string[];
  format: 'pdf' | 'excel' | 'csv';
  status: 'active' | 'paused' | 'error';
  timezone: string;
  deliveryMethod: 'email' | 'download' | 'both';
  autoSend: boolean;
  createdBy: string;
  createdAt: string;
  successCount: number;
  failureCount: number;
}

const mockScheduledReports: ScheduledReport[] = [
  {
    id: '1',
    name: 'Daily Player Summary',
    description: 'Individual performance summaries sent to players and parents',
    reportType: 'player',
    frequency: 'daily',
    nextRun: '2024-01-16T09:00:00Z',
    lastRun: '2024-01-15T09:00:00Z',
    recipients: ['players', 'parents'],
    format: 'pdf',
    status: 'active',
    timezone: 'America/New_York',
    deliveryMethod: 'email',
    autoSend: true,
    createdBy: 'Coach Sarah Wilson',
    createdAt: '2024-01-01T10:00:00Z',
    successCount: 45,
    failureCount: 2
  },
  {
    id: '2',
    name: 'Weekly Team Performance',
    description: 'Comprehensive team analytics and progress tracking',
    reportType: 'team',
    frequency: 'weekly',
    nextRun: '2024-01-21T17:00:00Z',
    lastRun: '2024-01-14T17:00:00Z',
    recipients: ['coaches', 'academy_admin'],
    format: 'excel',
    status: 'active',
    timezone: 'America/New_York',
    deliveryMethod: 'both',
    autoSend: true,
    createdBy: 'Coach Mike Johnson',
    createdAt: '2024-01-01T10:00:00Z',
    successCount: 12,
    failureCount: 0
  },
  {
    id: '3',
    name: 'Monthly Academy Overview',
    description: 'High-level academy performance and operational metrics',
    reportType: 'academy',
    frequency: 'monthly',
    nextRun: '2024-02-01T10:00:00Z',
    lastRun: '2024-01-01T10:00:00Z',
    recipients: ['academy_admin', 'stakeholders'],
    format: 'pdf',
    status: 'active',
    timezone: 'America/New_York',
    deliveryMethod: 'email',
    autoSend: true,
    createdBy: 'Academy Admin',
    createdAt: '2024-01-01T10:00:00Z',
    successCount: 3,
    failureCount: 0
  },
  {
    id: '4',
    name: 'Bi-weekly Progress Reports',
    description: 'Detailed progress reports for parents every two weeks',
    reportType: 'player',
    frequency: 'custom',
    customSchedule: '0 16 * * 1,4', // Every Monday and Thursday at 4 PM
    nextRun: '2024-01-18T16:00:00Z',
    lastRun: '2024-01-15T16:00:00Z',
    recipients: ['parents'],
    format: 'pdf',
    status: 'paused',
    timezone: 'America/New_York',
    deliveryMethod: 'email',
    autoSend: false,
    createdBy: 'Coach Sarah Wilson',
    createdAt: '2024-01-05T10:00:00Z',
    successCount: 8,
    failureCount: 1
  }
];

const timezones = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Australia/Sydney'
];

const recipientOptions = [
  { id: 'players', label: 'All Players' },
  { id: 'parents', label: 'All Parents' },
  { id: 'coaches', label: 'All Coaches' },
  { id: 'academy_admin', label: 'Academy Administrators' },
  { id: 'stakeholders', label: 'Stakeholders' }
];

export const ReportScheduling: React.FC = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [_selectedReport, setSelectedReport] = useState<ScheduledReport | null>(null);
  const [newSchedule, setNewSchedule] = useState({
    name: '',
    description: '',
    reportType: 'player' as const,
    frequency: 'weekly' as const,
    time: '09:00',
    timezone: 'America/New_York',
    recipients: [] as string[],
    format: 'pdf' as const,
    deliveryMethod: 'email' as const,
    autoSend: true
  });

  const handleCreateSchedule = () => {
    console.log('Creating schedule:', newSchedule);
    setIsCreateDialogOpen(false);
    // Reset form
    setNewSchedule({
      name: '',
      description: '',
      reportType: 'player',
      frequency: 'weekly',
      time: '09:00',
      timezone: 'America/New_York',
      recipients: [],
      format: 'pdf',
      deliveryMethod: 'email',
      autoSend: true
    });
  };

  const handleToggleStatus = (reportId: string) => {
    console.log('Toggle status for schedule:', reportId);
    // In real implementation, this would update the schedule status
  };

  const handleRunNow = (reportId: string) => {
    console.log('Run schedule now:', reportId);
    // In real implementation, this would trigger immediate execution
  };

  const handleDeleteSchedule = (reportId: string) => {
    console.log('Delete schedule:', reportId);
    // In real implementation, this would delete the schedule
  };

  const handleRecipientToggle = (recipientId: string) => {
    setNewSchedule(prev => ({
      ...prev,
      recipients: prev.recipients.includes(recipientId)
        ? prev.recipients.filter(id => id !== recipientId)
        : [...prev.recipients, recipientId]
    }));
  };

  const getStatusColor = (status: ScheduledReport['status']) => {
    switch (status) {
      case 'active': return 'default';
      case 'paused': return 'secondary';
      case 'error': return 'destructive';
    }
  };

  const getStatusIcon = (status: ScheduledReport['status']) => {
    switch (status) {
      case 'active': return CheckCircle;
      case 'paused': return Pause;
      case 'error': return AlertCircle;
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  };

  const getFrequencyDisplay = (report: ScheduledReport) => {
    if (report.frequency === 'custom') {
      return `Custom (${report.customSchedule})`;
    }
    return report.frequency.charAt(0).toUpperCase() + report.frequency.slice(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Report Scheduling</h2>
          <p className="text-muted-foreground">
            Automate report generation and delivery
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)} className="flex items-center space-x-2">
          <Plus className="w-4 h-4" />
          <span>New Schedule</span>
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Schedules</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {mockScheduledReports.filter(r => r.status === 'active').length}
            </div>
            <p className="text-xs text-muted-foreground">
              Running automatically
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Reports This Week</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">42</div>
            <p className="text-xs text-muted-foreground">
              +12% from last week
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">98.5%</div>
            <p className="text-xs text-muted-foreground">
              Last 30 days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Recipients Reached</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">156</div>
            <p className="text-xs text-muted-foreground">
              Unique recipients
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Scheduled Reports List */}
      <div className="space-y-4">
        {mockScheduledReports.map((report) => {
          const StatusIcon = getStatusIcon(report.status);
          return (
            <Card key={report.id}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-semibold text-lg">{report.name}</h3>
                        <Badge variant={getStatusColor(report.status)} className="flex items-center space-x-1">
                          <StatusIcon className="w-3 h-3" />
                          <span>{report.status}</span>
                        </Badge>
                        <Badge variant="outline">
                          {getFrequencyDisplay(report)}
                        </Badge>
                      </div>
                      
                      <p className="text-muted-foreground mb-3">{report.description}</p>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-muted-foreground">Next Run</div>
                          <div className="font-medium">{formatDate(report.nextRun)}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Last Run</div>
                          <div className="font-medium">
                            {report.lastRun ? formatDate(report.lastRun) : 'Never'}
                          </div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Recipients</div>
                          <div className="font-medium">{report.recipients.length} groups</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Success Rate</div>
                          <div className="font-medium">
                            {Math.round((report.successCount / (report.successCount + report.failureCount)) * 100)}%
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 mt-3 text-sm text-muted-foreground">
                        <span>Format: {report.format.toUpperCase()}</span>
                        <span>•</span>
                        <span>Delivery: {report.deliveryMethod}</span>
                        <span>•</span>
                        <span>Timezone: {report.timezone}</span>
                        <span>•</span>
                        <span>Auto-send: {report.autoSend ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch 
                      checked={report.status === 'active'}
                      onCheckedChange={() => handleToggleStatus(report.id)}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRunNow(report.id)}
                      className="flex items-center space-x-1"
                    >
                      <Play className="w-4 h-4" />
                      <span>Run Now</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedReport(report)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteSchedule(report.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Create Schedule Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Report Schedule</DialogTitle>
            <DialogDescription>
              Set up automated report generation and delivery
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Schedule Name</Label>
                <Input
                  placeholder="Enter schedule name"
                  value={newSchedule.name}
                  onChange={(e) => setNewSchedule(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Report Type</Label>
                <Select 
                  value={newSchedule.reportType} 
                  onValueChange={(value) => setNewSchedule(prev => ({ ...prev, reportType: value as any }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="player">Player Report</SelectItem>
                    <SelectItem value="team">Team Report</SelectItem>
                    <SelectItem value="academy">Academy Report</SelectItem>
                    <SelectItem value="session">Session Report</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Describe what this schedule will do"
                value={newSchedule.description}
                onChange={(e) => setNewSchedule(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select 
                  value={newSchedule.frequency} 
                  onValueChange={(value) => setNewSchedule(prev => ({ ...prev, frequency: value as any }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Time</Label>
                <Input
                  type="time"
                  value={newSchedule.time}
                  onChange={(e) => setNewSchedule(prev => ({ ...prev, time: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Timezone</Label>
                <Select 
                  value={newSchedule.timezone} 
                  onValueChange={(value) => setNewSchedule(prev => ({ ...prev, timezone: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timezones.map((tz) => (
                      <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-3">
              <Label>Recipients</Label>
              <div className="grid grid-cols-2 gap-2">
                {recipientOptions.map((option) => (
                  <div key={option.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={option.id}
                      checked={newSchedule.recipients.includes(option.id)}
                      onCheckedChange={() => handleRecipientToggle(option.id)}
                    />
                    <Label htmlFor={option.id} className="text-sm">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Export Format</Label>
                <Select 
                  value={newSchedule.format} 
                  onValueChange={(value) => setNewSchedule(prev => ({ ...prev, format: value as any }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="excel">Excel</SelectItem>
                    <SelectItem value="csv">CSV</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Delivery Method</Label>
                <Select 
                  value={newSchedule.deliveryMethod} 
                  onValueChange={(value) => setNewSchedule(prev => ({ ...prev, deliveryMethod: value as any }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email Only</SelectItem>
                    <SelectItem value="download">Download Only</SelectItem>
                    <SelectItem value="both">Email & Download</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="auto-send"
                checked={newSchedule.autoSend}
                onCheckedChange={(checked) => setNewSchedule(prev => ({ ...prev, autoSend: checked as boolean }))}
              />
              <Label htmlFor="auto-send">
                Automatically send reports when generated
              </Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateSchedule}>
              Create Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};